type ConstantsParams = {
  fund_name: string
}

export type WidgetConstantGetter = (params: ConstantsParams) => {
  widgetTitle: React.ReactNode
  descriptions?: {
    widget?: React.ReactNode
    other?: React.ReactNode
  } & { [key: string]: React.ReactNode }
}

// ... Section --> Widget --> Constants
type Constants = Record<string, Record<string, WidgetConstantGetter>>

export const labels = {
  fund_level: {
    summary: () => ({
      widgetTitle: 'Summary',
    }),
    track_record_since_inception: () => ({
      widgetTitle: 'Track Record Since Inception',
      descriptions: {
        widget: 'Fund NAV since inception rebased to 100',
      },
    }),
    fund_performance_metrics: () => ({
      widgetTitle: 'Fund Performance Metrics',
      descriptions: {
        widget:
          'Total Return over specified period, where 1m relates to exactly 1 month prior to selected end date, etc.',
        ['Target 6% Arithmetic Return']: 'Annualised target return of fund',
        ['Geometric Annualised Return']: (
          <>
            Geometric return over specified period, annualised using the exact number of days
            between the start and end date.
            <br />
            1m relates to exactly 1 month prior to selected end date
          </>
        ),
        ['Arithmetic Annualised Return']: 'Average return over specified period',
        ['Annualised Volatility']: 'Annualised volatility over specified period',
        ['Return / Volatility']:
          'Period geometric annualised return divided by respective annualised volatility',
        ['Skew']: (
          <>
            Measures the degree of asymmetry in statistical return distribution, through
            differentiating extreme values in one tail against the other tail.
            <br />
            Positive (negative) skewness implies that there are more extremely larger gains
            (losses).
          </>
        ),
        ['Max Drawdown']: 'Maximum drawdown over rolling drawdown period',
        ['Benchmark Correlation']: 'Correlation between fund and MSCI World over specified period',
        ['Benchmark Beta']: 'Beta between fund and MSCI World over specified period',
        ['BCOM beta']: 'Beta to Bloomberg commodity index',
      },
    }),
    buckets: () => ({
      widgetTitle: 'Buckets',
      descriptions: {
        beta: 'Underlying correlation to MSCI World greater than +0.4',
        cis: 'Cross-holdings',
        diversifier: 'Underlying correlation to MSCI World between +0.4 and -0.4',
        income: 'Underlying fixed income',
        protection: 'Underlying correlation to MSCI World greater than -0.4',
      },
    }),
    liquidity: () => ({
      widgetTitle: 'Liquidity',
      descriptions: {
        widget:
          'Cost to liquidate all positions, broken down by bucket. This is based off b/o spreads and fixed exit costs on swaps held',
      },
    }),
    tracking: ({ fund_name }) => {
      const widgetDescriptions = {
        FCARF: 'Sources of tracking difference between fund and Benchmark',
      }
      return {
        widgetTitle: 'Tracking',
        descriptions: {
          widget:
            widgetDescriptions[fund_name as keyof typeof widgetDescriptions] ??
            'Sources of tracking difference between fund and SPTR',
        },
      }
    },
    stress_test: () => ({
      widgetTitle: 'Stress Test',
      descriptions: {
        widget:
          "The analysis stresses the fund's underlying in a COVID sell off scenario. This is computed at the end of each month and will not display any results if the latest date is prior to month end.",
      },
    }),
    scenario_analysis: () => ({
      widgetTitle: 'Scenario Analysis',
      descriptions: {
        widget:
          'Bucket scenario analysis based on weights at time T and % market moves during crises',
      },
    }),
  },
  income: {
    duration: () => ({
      widgetTitle: 'Duration',
      descriptions: {
        widget: 'Weighted average maturity, weighted by notional',
      },
    }),
    rating: () => ({
      widgetTitle: 'Rating',
      descriptions: {
        widget:
          'Weighted average (by notionals) S&P credit ratings of bonds held in collateral book. This ignores fund cross holdings and AMCs',
      },
    }),
    locked_yield: () => ({
      widgetTitle: 'Locked Yield',
      descriptions: {
        widget: 'Weighted average yield, weighted by notional',
      },
    }),
    eod_cash: () => ({
      widgetTitle: 'EOD Cash',
      descriptions: {
        widget: 'Cash, converted to base currency as a % of NAV',
      },
    }),
    costs_over_income: () => ({
      widgetTitle: 'Funding Costs and Income from Collateral Book',
      descriptions: {
        widget: null,
      },
    }),
    breakdown_per_issuer: () => ({
      widgetTitle: 'Breakdown Per Issuer',
      descriptions: {
        widget:
          'Yield contribution per asset to total yield sum and notional breakdown per asset  in base currency ',
      },
    }),
  },
  beta: {
    options: () => ({
      widgetTitle: 'Options',
      descriptions: {
        widget: 'Current beta exposure',
        position_delta_risk_weight: 'Delta-adjusted notional weight',
        moneyness_distance: 'How far out / in the money the individual option is',
        position_value_base_over_nav:
          'Current option value in portfolio as a portion of current NAV',
      },
    }),
    detailed_regional_breakdown: () => ({
      widgetTitle: 'Detailed Regional Breakdown',
      descriptions: {
        widget: "Breakdown of the fund's underlying beta bucket, inclusive of look through",
      },
    }),
    aggregated_by_region: () => ({
      widgetTitle: 'Aggregated by Region',
      descriptions: {
        widget: 'Beta aggregated by region, inclusive of look-through',
      },
    }),
    stresstest_gryt: () => ({
      widgetTitle: 'GRY (T)',
      descriptions: {
        widget:
          'GRY based on underlying autocall spot moves, assuming no reinvestment of maturing investments',
      },
    }),
    stresstest_gryt_minus1: () => ({
      widgetTitle: 'GRY (T-1)',
      descriptions: {
        widget:
          'GRY based on underlying autocall beta spot moves at speficied start date, assuming no reinvestment of maturing investments',
      },
    }),
    /**
     * Used as a composite
     */
    barriers: () => ({
      widgetTitle: '',
    }),
    /**
     * Used in unique ic report component
     */
    cover_to_capital_preservation: () => ({
      widgetTitle: 'Cover to Capital Preservation',
      descriptions: {
        widget:
          'The level by which the underlying equity index may fall, before capital is eroded, assuming each investments runs to its maturity',
      },
    }),
    /**
     * Used in unique ic report component
     */
    cover_to_capital_growth: () => ({
      widgetTitle: 'Cover to Capital Growth',
      descriptions: {
        widget:
          "The level by which the underlying equity index may fall, before capital growth is zero, assuming each investments runs to it's maturity",
      },
    }),
    scenario_analysis: ({ fund_name }) => {
      const widgetDescriptions = {
        FCDGF:
          'Beta bucket scenario analysis based on weights at time T and % market moves during crises',
      }

      return {
        widgetTitle: 'Scenario Analysis',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
  },
  diversifiers: {
    attribution: ({ fund_name }) => {
      const widgetDescriptions = {
        FCCAR: 'Time series of attribution at underlying strategy level',
        FCLA1: 'Attribution at asset level',
      }

      return {
        widgetTitle: 'Attribution',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
    factor_performance_attribution_5yr: ({ fund_name }) => {
      const widgetDescriptions = {
        FCPGF: 'Time series of attribution at risk premia level',
        FCAGF: 'Time series of attribution at risk premia level',
        FCDGF: 'Time series of attribution at risk premia level',
        FCCAR: 'Time series of attribution at underlying strategy level',
        FCARF: 'Time series of attribution per long and short leg',
        FCLA1: 'Time series of attribution per long and short leg',
      }

      return {
        widgetTitle: 'Factor Performance Attribution 5-Year',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
    monthly_performance_attribution_5yr: ({ fund_name }) => {
      const widgetDescriptions = {
        FCCAR: 'Time series of attribution per month at underlying strategy level',
        FCARF: 'Time series of cumulative monthly attribution per long and short leg',
      }

      return {
        widgetTitle: 'Monthly Performance Attribution 5-Year',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
    yearly_performance_attribution_5yr: ({ fund_name }) => {
      const widgetDescriptions = {
        FCARF: 'Times series of year by year attribution per long and short leg',
      }

      return {
        widgetTitle: 'Yearly Performance Attribution - Since 1999',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
    perf_contribution: ({ fund_name }) => {
      const widgetDescriptions = {
        FCARF: 'Performance contribution based on static long term weights',
      }
      return {
        widgetTitle: 'Perf Contribution 5-Year',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
    metrics: ({ fund_name }) => {
      const widgetDescriptions = {
        FCPGF: 'Portfolio weighted risk premia performance, inclusive of look-through',
        FCAGF: 'Portfolio weighted risk premia performance, inclusive of look-through',
        FCDGF: 'Portfolio weighted risk premia performance, inclusive of look-through',
        FCARF: '5 year weighted performance metrics',
        FCLA1: '5 year weighted performance metrics',
      }

      return {
        widgetTitle: 'Diversifier 5Y Ex Ante Performance Metrics',
        descriptions: {
          widget: widgetDescriptions[fund_name as keyof typeof widgetDescriptions],
        },
      }
    },
    us_rates_volatility_drivers: () => ({
      widgetTitle: 'US Rates Volatility Drivers',
      descriptions: {
        widget: 'Underlying greeks exposure of diversifier holding - US Rates Volatility',
      },
    }),
    scenario_analysis: () => ({
      widgetTitle: 'Ex Ante Diversifier Scenario Analysis',
      descriptions: {
        widget:
          'Diversifier bucket scenario analysis based on weights at time T and % market moves during crises',
      },
    }),
    cta_exposure: () => ({
      widgetTitle: 'CTA Exposure',
    }),
  },
  protection: {
    options: () => ({
      widgetTitle: 'Options',
      descriptions: {
        widget: 'Current protection exposure',
        position_delta_risk_weight: 'Delta-adjusted notional weight',
        moneyness_distance: 'How far out / in the money the individual option is',
        position_value_base_over_nav:
          'Current option value in portfolio as a portion of current NAV',
      },
    }),
    breakdown: () => ({
      widgetTitle: 'Breakdown',
    }),
    scenario_analysis: () => ({
      widgetTitle: 'Scenario Analysis',
      descriptions: {
        widget:
          'Protection bucket scenario analysis based on weights at time T and % market moves during crises',
      },
    }),
  },
  commodities: {
    curves: () => ({
      widgetTitle: 'Curves',
      descriptions: {
        widget: 'Underlying strategy commodity curves',
      },
    }),
    backwardation: () => ({
      widgetTitle: 'Backwardation 3-month / 6-month',
      descriptions: {
        widget: 'Underlying strategy commodity backwardation',
      },
    }),
    bear_bull: () => ({
      widgetTitle: 'BCOM Bear / Bull',
      descriptions: {
        widget: 'Bull and bear scenarios specific to Bloomberg Commodity Index',
      },
    }),
    scenario_analysis: ({ fund_name }) => ({
      widgetTitle: `${fund_name} Performance in BCOM Bear / Bull`,
      descriptions: {
        widget: 'Commodity carry performance during BCOM bull and bear scenarios',
      },
    }),
  },
} as const satisfies Constants
